<script setup lang="ts">
import { computed, defineEmits, ref } from 'vue';
import { useRouter } from 'vue-router';
import { JobRoles, UserResponseDto } from '@clearbanc/baus-sdk';
import { JobTitles, ownerJobTitles } from '@clearbanc/data-common-types';
import { RouteNames } from '@/data/routes';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

import Dialog from 'primevue/dialog';
import DSButton from 'primevue/button';
import UserInviteForm from '@/pages/users/components/UserInviteForm.vue';
import CheckmarkWithBorderIcon from '@/assets/icons/checkmark-with-border.svg';

// -----
// Setup
// -----

const router = useRouter();

// -------------
// Props & Emits
// -------------

const props = defineProps<{
  visible: boolean;
  isBusinessOwnersPage?: boolean;
}>();

// Provide a default value for isBusinessOwnersPage if undefined
const isBusinessOwnersPage = computed(() => !!props.isBusinessOwnersPage);

const emit = defineEmits<{
  closeModal: [];
}>();

// -----
// State
// -----

const showSuccessContent = computed(() => !!invitedUser.value);
const invitedUser = ref<UserResponseDto>();

// -------
// Methods
// -------

const isOwnerJobTitle = computed<boolean>(() => {
  if (!invitedUser.value) return false;

  return ownerJobTitles.includes(invitedUser.value.jobTitle as JobTitles);
});

const hasOwnerRole = computed<boolean>(() => {
  if (!invitedUser.value) return false;

  return invitedUser.value.jobRoles.includes(JobRoles.Owner);
});

const closeModal = () => {
  // Business Owners Page - Redirect if invitedUser is set as a business ownership and the route is OWNERS.NEW
  if (
    props.isBusinessOwnersPage &&
    (isOwnerJobTitle.value || hasOwnerRole.value)
  ) {
    router.push({ name: RouteNames.OWNERS.INDEX });
    return;
  }

  // Teams Page - Reset state when closing
  invitedUser.value = undefined;
  emit('closeModal');
};

const handleInviteSuccess = async (user: UserResponseDto) => {
  invitedUser.value = user;
};

const handleInviteCancel = () => {
  analytics.track(SEGMENT_EVENTS.if_send_invite_cancelled);
  closeModal();
};

const inviteAnotherUser = () => {
  invitedUser.value = undefined;
};
</script>

<template>
  <Dialog
    :visible="props.visible"
    :draggable="false"
    :header="showSuccessContent ? undefined : 'Invite New Member'"
    modal
    :closable="!showSuccessContent"
    :closeOnEscape="false"
    @update:visible="closeModal"
    style="width: 700px"
  >
    <!-- Success Content -->
    <template v-if="showSuccessContent" #header>
      <div class="p-dialog-title flex flex-col items-center">
        <CheckmarkWithBorderIcon />
        <span>Invitation Email Sent</span>
      </div>
    </template>

    <div v-if="showSuccessContent" class="text-center py-6">
      <p class="text-gray-500">
        We sent an invitation to {{ invitedUser?.email }}. The new member can
        join your team by following the provided instructions.
      </p>
    </div>

    <!-- Success Footer -->
    <template v-if="showSuccessContent" #footer>
      <div class="flex justify-center">
        <DSButton
          label="Done"
          @click="closeModal"
          class="p-button-outlined w-full"
        />
        <DSButton
          label="Invite another user"
          @click="inviteAnotherUser"
          class="w-full"
        />
      </div>
    </template>

    <!-- Invite Form Content -->
    <UserInviteForm
      v-if="!showSuccessContent"
      :is-business-owners-page="isBusinessOwnersPage"
      @success="handleInviteSuccess"
      @cancel="handleInviteCancel"
    />
  </Dialog>
</template>
